/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import DtModObservable from '../Game/DtModObservable';
import Game from '../Game/Game';
import GameSetup from '../Game/GameSetup';
import RecoveryManager from '../Game/RecoveryManager';
import PaylinesDefinitionComponent from '../Paylines/PaylinesDefinitionComponent';
import Communicator from '../Server/Communicator';
import { ISlotGamePlayResult } from '../Server/Object/BaseGamePlayResult';
import IPlayResultComponent from '../Server/Object/IPlayResultComponent';
import IPlayResultData from '../Server/Object/IPlayResultData';
import PlayResultComponent from '../Server/PlayResultComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateManager from '../StateManager/StateManager';
import StateReference from '../StateManager/StateReference';
import ISymbolData from '../StreakTracker/ISymbolData';
import SymbolSet from '../Symbol/SymbolSet';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import { ContextKey } from '../Utility/Component/Configurator/ContextKey';
import IPopupManager from '../Utility/Component/Popup/IPopupManager';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import IReelManager from './IReelManager';
import Reel from './Reel';
import { ReelEvent } from './ReelEvent';
import ReelSymbol from './Symbol';
import SymbolSubstitutionBase from './SymbolSubstitutionBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Manages a set of [[Reel]]s that represent a slot game. */
@cc._decorator.ccclass
export default class ReelManager extends StateDependencyComponent implements IReelManager
{
    /** The states on which the reels should be spinning. */
    @property({ type: [StateReference], visible: true })
    protected _spinStateRefs: StateReference[] = [new StateReference(StateID.Spinning, true)];

    /** The states on which the reels should start spinning. */
    @property({ type: [StateReference], visible: true })
    private _startSpinState: StateReference[] = [new StateReference(StateID.Request, true)];

    /** The state on which the ReelManager should fast forward to the state being recovered. */
    @property({ type: StateReference, visible: true })
    private _recoveryState: StateReference = new StateReference(StateID.CheckRecall);

    /** The ID of the reel set to request from the RGS. */
    @property({ type: cc.Integer, visible: true })
    private _reelSetID: Number = 1;

    /** The symbol set to request on the reel strips for the RGS. */
    @property({ type: cc.Integer, visible: true })
    private _symbolSetID: Number = 1;

    /** The symbol string used to represent half-stops on these reels, if any. */
    @property({ visible: true })
    private _halfStopSymbol: string = "";

    /** The [[Reel]]s controlled by this ReelManager. */
    @property ([Reel])
    protected reels: Reel[] = [];
    /** Accessor for the [[Reel]]s controlled by this ReelManager. */
    public get Reels(): Reel[] { return this.reels; }

    /** The stop delays for each reel. Should mirror the reels collection. */
    @property ([cc.Float])
    private stopDelays: number[] = [];
    /** Accessor for the stop delays used for each reel. */
    public get StopDelays(): number[] { return this.stopDelays; }

    /** DEV ONLY. Should ReelManager debug info be rendered. */
    @property(Boolean)
    private debugDraw: boolean = false;

    public static readonly OnSpinStart: string = 'ReelManager.OnSpinStart';
    public static readonly OnMinSpinTimeComplete: string = 'ReelManager.OnMinSpinTimeComplete';

    private _isSpinTimeComplete: boolean = false;

    public CanQuickStop: boolean = true;

    /** @hidden */
    private _gameRef: InjectableComponent<Game> = new InjectableComponent();
    /** @hidden */
    private _communicatorRef: InjectableComponent<Communicator> = new InjectableComponent();
    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    public get GameSetup(): GameSetup { return this._gameSetupRef.Value; }

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _symbolSetRef: InjectableComponent<SymbolSet> = new InjectableComponent();
    /** @hidden */
    private _recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();
    /** @hidden */
    private _popupManagerRef: InjectableComponent<IPopupManager> = new InjectableComponent();
    /** @hidden */
    private _paylineDefinitionRef: InjectableComponent<PaylinesDefinitionComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, 'Payline.Definition');
    public get PaylineCount(): number
    {
        return this._paylineDefinitionRef.WithValue(paylineDef => paylineDef.PaylineCount, 0);
    }

    /** @hidden */
    private _playResultRef: InjectableComponent<IPlayResultComponent> = new InjectableComponent();
    /** @hidden */
    private _dtModRef: InjectableComponent<DtModObservable> = new InjectableComponent();

    /** @hidden */
    private _origReelStrips: string[] = null;
    /** @hidden */
    private _symbolSubstitution: SymbolSubstitutionBase = null;
    /** @hidden */
    private _spinDuration: number = 0;
    /** @hidden */
    private stoppedReels: number = 0;

    /** The number of reels used on the machine controlled by this manager. */
    public get ReelCount(): number { return this.reels.length; }
    /** How many symbols yet to be cycled before the reel lands. */
    public get SymbolsRemaining(): number { return this.reels.filter(reel => !reel.HasLanded).length; }

    private _stateManagers: StateManager[] = [];

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._gameRef.SetContext(Game);
        this._communicatorRef.SetContext(Communicator);
        this._gameSetupRef.SetContext(GameSetup);
        this._symbolSetRef.SetContext(SymbolSet);
        this._recoveryManagerRef.SetContext(RecoveryManager);
        this._popupManagerRef.SetContext(ContextKey.POPUP_MANAGER);
        this._paylineDefinitionRef.SetContext(PaylinesDefinitionComponent, { InjectOnce: true });
        this._playResultRef.SetContext(PlayResultComponent);
        this._dtModRef.SetContext(DtModObservable);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(
            this._startSpinState,
            this._spinStateRefs,
            this._recoveryState,
            this._gameSetupRef.Value.SetupOnState
        );

        this._gameSetupRef.Value.OnSetupReceived(() => this.onSetupReceived());
        this._playResultRef.Value.OnResult(() =>
            {
                if (!this.IsMinimumSpinReached) { return; }

                this.reels.forEach(reel => reel.ResetElapsedTime());

                if (!this._spinStateRefs.some(StateReference.IsCurrentFilter)) { return; }

                this.Stop();
            });

        this._startSpinState.forEach(stateRef => this.addStateManager(stateRef));
        this._spinStateRefs.forEach(stateRef => this.addStateManager(stateRef));
        this.addStateManager(this._recoveryState);
    }

    private addStateManager(stateRef: StateReference): void
    {
        if (!stateRef.Value) { return; }
        const stateManager: StateManager = stateRef.Value.StateManager;
        if (!stateManager) { return; }

        if (this._stateManagers.indexOf(stateManager) > -1) { return; }
        stateManager.OnAllStateChange(() => this.updateStateStatus());
        this._stateManagers.push(stateManager);
    }

    private onSetupReceived = (): void =>
    {
        this.init();

        this._communicatorRef.Value
            .RequestReelStrips(this._reelSetID, this._symbolSetID, this.onReceiveReelStrips);
    }

    /** @hidden */
    private init(): void
    {
        this.initializeReels();
        this.initializeDebug();
    }

    /** @hidden */
    protected update(dt: number): void
    {
        super.update(dt);

        dt = this._dtModRef.Value.Multiply(dt);

        if (this._isSpinState && this.IsAllStopped())
        {
            const playResult: IPlayResultComponent = this._gameRef.Value.CurrentPlayResult;
            const resultData: IPlayResultData = playResult.ResultData;
            if (!resultData) { return; }

            if (resultData.HasBonus())
            {
                this.completeStateOptionalNext(this._currentSpinState.Value);
            }
            else
            {
                this.completeState(this._currentSpinState.Value);
            }
        }

        if (this._isAnySpinState && !this._isSpinTimeComplete)
        {
            if (this.IsMinimumSpinReached)
            {
                this.node.dispatchEvent(new cc.Event.EventCustom(ReelManager.OnMinSpinTimeComplete, true));
                this._isSpinTimeComplete = true;
            }
            else
            {
                this._spinDuration += dt;
            }
        }
    }

    /** @hidden */
    protected onDestroy(): void
    {
        this._stateManagers.forEach(stateManager => stateManager.OffAllStateChange(() => this.updateStateStatus()));
    }

    public get IsMinimumSpinReached(): boolean
    {
        return this._spinDuration >= this._gameSetupRef.Value.MinSpinDelay;
    }

    /** Sets each controlled [[Reel]] to spin. */
    public Spin(): void
    {
        // CLog.log("Spin initiated", LogTypes.Reel, this);
        this._spinDuration = 0;
        this.stoppedReels = 0;

        this.CanQuickStop = true;
        this._isSpinTimeComplete = false;
        this._lastLandedCount = 0;

        this.node.dispatchEvent(new cc.Event.EventCustom(ReelManager.OnSpinStart, true));

        this._cachedSymbolData = null;
        this.reels.forEach((reel) =>
        {
            if (reel.IsLocked || !reel.IsEnabled)
            {
                this.stoppedReels++;
            }
            else
            {
                reel.Spin();
            }
        });
    }

    /** Sets off the stop process to stop each reel from left to right after each stop delay has elapsed. */
    public Stop(): void
    {
        const result: ISlotGamePlayResult = this._gameRef.WithValue(game =>
            {
                const playResult: IPlayResultComponent = game.CurrentPlayResult;
                if (!playResult) { return null; }
                const resultData: IPlayResultData = playResult.ResultData;
                if (!resultData) { return null; }
                return resultData.GetCurrentGameResult();
            });

        if (!result) { return; }

        const stops: number[] = result.reelStopPosition;

        if (this._recoveryManagerRef.Value.Recovering)
        {
            this.reels.forEach((reel, index) => reel.StopImmediate(stops[index]));
        }
        else
        {
            this.stopAt(stops);
        }
    }

    /** Stops each [[Reel]] immediately, regardless of stop delay. */
    public StopImmediate(): void
    {
        let stops: number[] = [];
        let currentPlay: IPlayResultData = this._gameRef.Value.CurrentPlayResult.ResultData;
        if (currentPlay != null)
        {
            stops = currentPlay.GetBaseGameResult().reelStopPosition;
        }
        else
        {
            let lastPlay: any = this._gameSetupRef.Value.LastPlayState.playResult.BaseGamePlayResult.SlotGamePlayResult;
            stops = lastPlay.reelStopPosition;
            this._recoveryManagerRef.Value.RecoveryCurrentPlayID++;
        }

        this.reels.forEach((reel, index) => reel.StopImmediate(stops[index]));
    }

    /**
     * Assembles a collection of [[ISymbolData]] which represents all visible symbols on the [[Reel]] faces.
     * @returns The collection of [[ISymbolData]].
     */
    public GetCurrentReelsData(): ISymbolData[]
    {
        const symbols: ISymbolData[] = [];
        const landedCount: number = this.reels.filter(reel => reel.HasLanded).length;

        if (this._lastLandedCount === landedCount
            && this._cachedSymbolData !== null) { return this._cachedSymbolData; }

        for (let reelIndex: number = 0; reelIndex < this.reels.length; reelIndex++)
        {
            const reel: Reel = this.reels[reelIndex];
            if (!reel.HasLanded) { continue; }

            for (let symbolNode of reel.VisibleSymbols)
            {
                const symbol: ReelSymbol = symbolNode.getComponent(ReelSymbol);
                if (!symbol.IsReelFace) { continue; }

                const matchedPaylines: number[] = this._paylineDefinitionRef.WithValue(paylineDef =>
                    {
                        return paylineDef.GetPaylinesForOffset(reelIndex, symbol.OffSet);
                    },
                    () =>
                    {
                        CLog.warn('No payline definition found. Using winning payline offsets.',
                            LogTypes.Initialization, this);
                        return this.getOffsetPaylines(reelIndex, symbol.OffSet);
                    });

                symbols.push(
                    {
                        IsActivePayline: matchedPaylines && matchedPaylines.length > 0,
                        Node: symbol.node,
                        OffSet: symbol.OffSet,
                        Payline: matchedPaylines,
                        ReelIndex: reelIndex,
                        SymbolId: symbol.SymbolData ? symbol.SymbolData.SymbolId : null
                    }
                );
            }
        }

        this._cachedSymbolData = symbols;
        this._lastLandedCount = landedCount;
        return symbols;
    }
    private _lastLandedCount: number = 0;
    private _cachedSymbolData: ISymbolData[] = null;

    private getOffsetPaylines(reelIndex: number, offset: number): number[]
    {
        const activePaylines: number[] = [];
        const activePaylineOffsets: number[][]
            = this._gameSetupRef.Value.LastPlayResult.GetCurrentGameResult().paylineOffsets;

        let paylineIndex: number = -1;
        for (const offsets of activePaylineOffsets)
        {
            paylineIndex++;
            if (offsets[reelIndex] !== offset) { continue; }
            activePaylines.push(paylineIndex);
        }

        return activePaylines;
    }

    /**
     * Emits an event message on each reel node.
     * @param message The event string to dispatch.
     */
    public SendMessageToReels(message: string): void
    {
        for (let reel of this.reels)
        {
            reel.node.dispatchEvent(new cc.Event.EventCustom(message, true));
        }
    }

    /**
     * Attached to event on each reel.
     * @param reelEvent [[ReelEvent]] to execute on.
     * @param fnOnReelEvent Function to execute.
     */
    public OnReelEvent(reelEvent: ReelEvent, fnOnReelEvent: (event: cc.Event.EventCustom) => void): void
    {
        this.reels.forEach(reel => reel.OnReelEvent(reelEvent, fnOnReelEvent));
    }

    /**
     * Attached to event on each reel for one execution.
     * @param reelEvent [[ReelEvent]] to execute on.
     * @param fnOnReelEvent Function to execute.
     */
    public OnReelEventOnce(reelEvent: ReelEvent, fnOnReelEvent: (event: cc.Event.EventCustom) => void): void
    {
        this.reels.forEach(reel => reel.OnReelEventOnce(reelEvent, fnOnReelEvent));
    }

    /** @hidden */
    private stopAt(stopPositions: number[]): void
    {
        this.reels.forEach((reel, index) => reel.Stop(stopPositions[index]));
    }

    /** Stops the reels at the last play result from server. */
    protected stopAtLastPlay(): void
    {
        let lastPlay: any = this._gameSetupRef.Value.LastPlayState.playResult.BaseGamePlayResult.SlotGamePlayResult;
        let stops: number[] = lastPlay.reelStopPosition;
        this.stopAt(stops);
    }

    /**
     * Fetches the [[Reel]] at the specified column.
     * @param reelIndex The index of the [[Reel]] to retrieve.
     * @returns The specified [[Reel]].
     */
    public GetReel(reelIndex: number): Reel
    {
        return this.reels[reelIndex];
    }

    // #region Handlers

    private _isStartSpinState: boolean = false;
    private _isSpinState: boolean = false;
    private _currentSpinState: StateReference = null;
    private _isAnySpinState: boolean = false;

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (this._isStartSpinState)
        {
            this.Spin();
            this.completeState(event.State);
            return;
        }

        if (this._isSpinState)
        {
            if (this._symbolSubstitution
                && this._symbolSubstitution.IsContinuous
                && this._origReelStrips)
            {
                this.reels.forEach((reel, index) =>
                {
                    reel.OnReceiveReelStrip(
                        this._symbolSubstitution.SubstituteReelSymbols(this._origReelStrips[index].split(''), index)
                    );
                });
            }
            this.Stop();
            return;
        }

        if (event.State === this._recoveryState.Value)
        {
            this.StopImmediate();
            this.completeState(event.State);
        }
    }

    private updateStateStatus(): void
    {
        this._isStartSpinState = this._startSpinState.some((x) => x.Value.IsCurrentState());

        this._currentSpinState = this._spinStateRefs.find(state => state.IsCurrent());
        this._isSpinState = this._currentSpinState !== undefined;

        this._isAnySpinState = this._isStartSpinState || this._isSpinState;
    }

    /** @hidden */
    private onReceiveReelStrips = (aReelStrips: string[]): void =>
    {
        this._origReelStrips = aReelStrips;
        this._symbolSubstitution = this.getComponent(SymbolSubstitutionBase);

        if (this._symbolSubstitution)
        {
            this.reels.forEach((reel, index) =>
            {
                reel.OnReceiveReelStrip(
                    this._symbolSubstitution.SubstituteReelSymbols(aReelStrips[index].split(''), index)
                );
            });
        }
        else
        {
            this.reels.forEach((reel, index) => { reel.OnReceiveReelStrip(aReelStrips[index].split('')); });
        }

        this.completeState(this._gameSetupRef.Value.SetupOnState.Value);
    }

    // #endregion

    // #region Local Helpers

    /** @hidden */
    private initializeReels(): void
    {
        let diff: number = 0;

        this._gameSetupRef.WithValue(gameSetup =>
            {
                if (this.stopDelays[0] < gameSetup.MinSpinDelay)
                {
                    // Accurate to the millisecond. Floating points ruin everything.
                    diff = Math.round((gameSetup.MinSpinDelay - this.stopDelays[0]) * 1000) / 1000;
                }
            }, () => CLog.error('No GameSetup ref.', this, LogTypes.Initialization));

        this.reels.forEach((reel, index) =>
            {
                const stopDelay: number = this.stopDelays[index] + diff;
                reel.Initialize(index, stopDelay, this._symbolSetRef.Value, this._halfStopSymbol);
                reel.OnStopped(this.onReelStopped.bind(this));
            });

        this.reels[this.reels.length - 1]
                .OnStopped(() =>
                    {
                        this._popupManagerRef.WithValue(popupManager => popupManager.Disable());
                    });
    }

    /** @hidden */
    private initializeDebug(): void
    {
        for (let richText of this.node.getComponentsInChildren(cc.RichText))
        {
            richText.node.active = this.debugDraw;
        }
    }

    /** @hidden */
    public IsAllStopped(): boolean
    {
        return this.stoppedReels === this.reels.length;
    }

    /** @hidden */
    private onReelStopped = (): void =>
    {
        this.stoppedReels++;
    }

    // #endregion
}
